import {
  isEntityQueryTarget,
  type EntityQueryTarget,
} from "@/Boards/definition/entityColumns";
import type { KnownSourceKind } from "@/Boards/definition/stages";
import { timeZoneChoiceLabel } from "@/shared/datetime/timeZones";
import type {
  DryRunFailure,
  DryRunPanelReport,
  DryRunStageReport,
} from "@/shared/domain/boards/boardAuthoringService";
import {
  boardCalendarOf,
  declaredBoardCalendarSchema,
  timeZoneProblem,
  type BoardCalendar,
} from "@/shared/domain/boards/boardCalendar";
import {
  asBoardTimeRangeMode,
  boardTimeRangeLabel,
} from "@/shared/domain/boards/boardTimeRangeMode";
import { isEmptyText } from "@/shared/domain/boards/emptyText";
import { entityFieldDisplayName } from "@/shared/domain/boards/entityFieldNames";
import {
  convertInteractionValue,
  INTERACTION_VALUE_TYPE_WORDS,
  interactionValueTypeSchema,
  parseInteractionValue,
} from "@/shared/domain/boards/interactionValueCodec";
import {
  interactionValueText,
  listedLabelOf,
  listedOptionLabel,
} from "@/shared/domain/boards/interactionValueText";
import { panelPhrase } from "@/shared/domain/boards/panelCaption";
import { selectionPhrase } from "@/shared/domain/boards/selectionNames";
import { defaultInputIndex } from "@/shared/domain/boards/stageDefaultInput";
import {
  controlFilterPurpose,
  SINGLE_INPUT_STAGE_PURPOSES,
  STAGE_KIND_NAMES,
  type DerivedOrControlFilterKind,
} from "@/shared/domain/boards/stageKindWords";
import { declaredStageName } from "@/shared/domain/boards/stageName";
import type { CustomFieldRecord } from "@/shared/domain/customFields/CustomFieldRecord";
import {
  DAYS_OF_WEEK,
  type DayOfWeek,
} from "@/shared/domain/metrics/MetricRecord";
import { capitalizeFirst } from "@/shared/text/capitalizeFirst";

// The text the board confirmation cards show, in a module of its own so the
// card modules export only components.

/**
 * What failed, by the kind a dry-run failure is filed under, as the end of a
 * card line ("failed: its SQL has an error"). The failure's message is written
 * for the chat, which the card's note says receives it, so a line never shows
 * it. A kind with no words here leaves the line at "failed".
 */
const FAILURE_PHRASES: Readonly<Record<string, string>> = {
  "stage-sql-failed": "its SQL has an error",
  "sql-built-in-stages-express":
    "its SQL does what a built-in stage does; use that stage instead",
  "stage-failed": "it could not run",
  "stage-input-failed": "a stage it reads failed",
  "blocked-stage": "a stage it reads can't run",
  "bound-stage-invalid": "a stage it shows can't run",
  "unknown-stage": "it shows a stage that is not on the board",
  "unknown-input": "it reads a stage that is not on the board",
  "stage-cycle": "it reads its own rows through other stages",
  "incomplete-stage": "some of its settings are not filled in",
  "malformed-stage": "some of its settings are not valid",
  "malformed-panel": "some of its settings are not valid",
  "unknown-panel-kind": "it is not a chart, table or map",
  "duplicate-stage-id": "another stage in the board's JSON has the same ID",
  "duplicate-stage-name": "another stage has the same name",
  "duplicate-panel-id": "another panel in the board's JSON has the same ID",
  "combine-refused": "its inputs can't be combined as it asks",
  "stage-refused": "its settings do not fit the stage it reads",
  "control-undeclared": "it reads a control that is not on the board",
  "control-value-unusable": "a control it reads holds a value it can't use",
  "control-value-unsupported": "a control it reads holds a value it can't use",
  "control-filter-input-invalid": "the stage it filters can't run",
  "control-filter-entity-id-invalid":
    "a control filters it by a column that does not identify records",
  "control-filter-column-missing":
    "a control filters it by a column its rows do not have",
  "control-filter-column-type":
    "a control can't be compared with the column it filters",
  "control-filter-before-summary-unsupported":
    "a control can't filter the source it reads",
  "focus-before-summary-unsupported":
    "a selection can't filter the source it reads",
  "control-entity-lookup-failed": "a control's choices could not be looked up",
  "source-request-refused": "Roboto rejected its request",
  "request-not-completed": "a request to Roboto did not complete",
  "dry-run-timed-out": "the check ran out of time",
  "topic-scope-empty": "no session matches it in the board's time range",
  "topic-missing": "its topic is not in the sessions it reads",
  "topic-field-missing": "a field it reads is not in its topic",
  "topic-read-failed": "its topic data could not be read",
  "read-plan-request-failed": "its topic data could not be read",
  "engine-load-failed": "data processing in this browser did not start",
  "engine-memory-exhausted":
    "data processing in this browser ran out of memory",
  "engine-trapped": "data processing in this browser failed",
  "chart-field-missing": "the chart uses a column its data does not have",
  "chart-compile-failed": "the chart JSON is not valid",
  "chart-internal-error": "the chart could not be drawn",
  "chart-template-failed": "the chart could not be drawn",
  "chart-run-failed": "the chart could not be drawn",
  "selection-param-repeated-in-layer": "the chart could not be drawn",
  chart: "the chart could not be drawn",
  "pivot-failed": "the table could not be built",
  table: "the table could not be built",
  "map-input-refused": "the map can't draw its data",
  map: "the map can't draw its data",
  "detail-target-field-unavailable":
    "an interaction uses a column its data does not have",
  "interaction-field-unavailable":
    "an interaction uses a column its data does not have, or a column of the wrong type",
  "detail-target-computed-from-other-data":
    "an interaction opens rows from data the panel is not built from",
  "click-or-drag-refused": "a click or drag on it does nothing",
  "placement-section-unknown":
    "it is placed in a section that is not on the board",
};

/** A failed card line: `failed: <what failed>` by the failure's kind, or
 *  `failed` for a kind with no words of its own. */
function failedLine(failure: { kind: string }): string {
  const phrase = FAILURE_PHRASES[failure.kind];
  return phrase === undefined ? "failed" : `failed: ${phrase}`;
}

/**
 * Why a stage or panel failed, in the sentences the board shows a person
 * beside it: a stage's declaration problems, or a panel's settings issues.
 * Empty for a failure whose kind (`failedLine`) is all a person is told.
 */
export function failureReasons(failure: DryRunFailure | undefined): string[] {
  if (failure?.besideStage !== undefined) {
    return [failure.besideStage.message, ...failure.besideStage.issues].filter(
      (sentence) => sentence !== "",
    );
  }
  return failure?.besidePanel?.issues ?? [];
}

/** One stage's dry-run line: what failed, the controls it waits for a
 *  value in (`notCheckedLine`), or its rows (or that it ran without a row
 *  count: topic data, which a dry run does not decode) followed by what the
 *  check noted about it (`warningPhrase`). */
export function describeStageReport(report: DryRunStageReport): string {
  if (report.failure !== undefined) {
    return failedLine(report.failure);
  }
  if (report.waiting_for !== undefined) {
    return notCheckedLine(report.waiting_for.controls);
  }
  const rows =
    report.rows === undefined
      ? "rows not counted until the board opens"
      : countOf(report.rows, "row", "rows");
  return [rows, ...warningPhrases(report.warnings ?? [])].join("; ");
}

/** A panel's dry-run line: the controls it waits for a value in, its chart
 *  spec's and those of the stages it binds (`notCheckedLine`); what
 *  failed; or the row counts of the stages it binds (found in `stages`, the
 *  report's per-stage list), then what the check noted about the panel and
 *  those stages (`warningPhrase`) and its count of faults the saved board
 *  already has. */
export function describePanelReport(
  report: DryRunPanelReport,
  stages: readonly DryRunStageReport[],
): string {
  const boundStages = stages.filter(
    (stage) => stage.stage !== null && report.stages.includes(stage.stage),
  );
  if (report.status === "waiting") {
    return notCheckedLine([
      ...(report.waiting_for?.controls ?? []),
      ...boundStages.flatMap((stage) => stage.waiting_for?.controls ?? []),
    ]);
  }
  if (report.status === "failed") {
    const own = report.parse ?? report.failures[0];
    if (own !== undefined) {
      return failedLine(own);
    }
    // A stage's phrase says "its", meaning the stage, so a panel failed by a
    // stage it shows says so instead.
    return boundStages.some((stage) => stage.failure !== undefined)
      ? "failed: a stage it shows failed"
      : "failed";
  }
  const rowCounts = boundStages
    .flatMap((stage) =>
      stage.rows === undefined
        ? []
        : [`"${stage.stageName}": ${countOf(stage.rows, "row", "rows")}`],
    )
    .join(", ");
  const rowsLabel =
    rowCounts.length > 0 ? rowCounts : "rows not counted until the board opens";
  if (report.status !== "warnings") {
    return rowsLabel;
  }
  const savedFaults = report.saved_board_faults?.length ?? 0;
  return [
    rowsLabel,
    ...warningPhrases([
      ...report.warnings,
      ...boundStages.flatMap((stage) => stage.warnings ?? []),
    ]),
    ...(savedFaults > 0
      ? [
          `${countOf(savedFaults, "problem", "problems")} already in the saved board`,
        ]
      : []),
  ].join("; ");
}

/**
 * One line for a panel whose faults the saved board already has, which do
 * not block an update: `panelName` (`panelPhrase`, capitalized), then what
 * each fault is, then why, where the board words that for a person
 * (`failureReasons`). Undefined for a panel with none.
 */
export function describeSavedPanelFaults(
  report: DryRunPanelReport,
  panelName: string,
): string | undefined {
  const faults = report.saved_board_faults ?? [];
  if (faults.length === 0) {
    return undefined;
  }
  const phrases = [
    ...new Set(
      faults.map((fault) => FAILURE_PHRASES[fault.kind] ?? "a problem"),
    ),
  ];
  const reasons = [...new Set(faults.flatMap(failureReasons))];
  return [`${panelName}: ${phrases.join("; ")}.`, ...reasons].join(" ");
}

/** What `warnings` note, each kind once (`warningPhrase`). */
export function warningPhrases(warnings: readonly string[]): string[] {
  return [...new Set(warnings.map(warningPhrase))];
}

/**
 * What a dry-run warning notes, by the kind its text starts with
 * (`empty-result: Stage "x" returned no rows.`). The text after the kind is
 * written for the chat, so a card shows these words instead; a kind with no
 * words here reads as {@link NOTED_FOR_THE_CHAT}.
 */
const WARNING_PHRASES: Readonly<Record<string, string>> = {
  "empty-result": "a stage returned no rows",
  "result-truncated": "a stage returned more rows than the check reads",
  "topic-data-not-decoded": "topic data is not read until the board opens",
  "topic-time-range-empty": "a topic has no data in the time range",
  "section-selection-unpublished":
    "a section has a selection of its own that no panel in it sets",
  "placement-section-unknown":
    "a panel is placed in a section that is not on the board",
  "participation-selection-unknown":
    "a panel's selection settings change nothing",
  "participation-column-missing":
    "a panel follows a selection but has no column to match it on",
  "click-or-drag-refused": "a click or drag on a panel does nothing",
  "detail-target-fields-unverified":
    "an interaction's columns could not be checked",
  "chart-tooltip-unavailable": "some of a chart's data points have no tooltip",
  "chart-interaction-unavailable":
    "one of a chart's interactions runs on none of its data points",
  "mark-lineage-source-unverified":
    "a chart's data point description reads columns that could not be checked",
  "mark-lineage-temporal-unverified":
    "a chart's data point description reads times that could not be checked",
  "stage-drops-identity":
    "a stage drops the column that identifies what a click picks",
  "combine-keys-omit-identity":
    "a Combine stage drops the column that identifies what a click picks",
  "chart-rendered-no-marks": "a chart draws nothing",
  "chart-dataset-large": "a chart reads many rows and may be slow",
  "chart-rows-left-out": "a chart leaves some rows out",
  "chart-warning": "a chart noted a problem while drawing",
  "table-column-missing": "a table shows a column its data does not have",
};

const NOTED_FOR_THE_CHAT = "a note the chat receives";

/** The kind a dry-run warning's text starts with (`empty-result: ...`). */
const WARNING_KIND_PREFIX = /^([a-z][a-z0-9-]*): /;

/** What a dry-run warning notes, in words a person reads on the card. */
export function warningPhrase(warning: string): string {
  const kind = WARNING_KIND_PREFIX.exec(warning)?.[1];
  return (
    (kind === undefined ? undefined : WARNING_PHRASES[kind]) ??
    NOTED_FOR_THE_CHAT
  );
}

/** `no warnings`, `1 warning`, `3 warnings`. */
function countOf(count: number, singular: string, plural: string): string {
  if (count === 0) {
    return `no ${plural}`;
  }
  return count === 1 ? `1 ${singular}` : `${count} ${plural}`;
}

/**
 * How a card names the panels, stages and controls of the definitions it
 * shows, by the ids a request writes, never by the id itself:
 * - `panel`: as `panelPhrase` words it (`the panel "Speeds"`, `Panel 3`);
 * - `stage`: the stage's name in quotes, for a list (`"Speeds"`);
 * - `stagePhrase`: `the stage "Speeds"`, or for a control `the control
 *   "Region"`;
 * - `controlPhrase`: `the control "Region"`;
 * - `declaredStagePhrase`: a stage given as a declaration rather than by
 *   its id, as `stagePhrase` names it, or `a stage with no name`, `a
 *   control with no name`;
 * - `catalogField`: a field of a catalog by its display name, as the
 *   control bar shows it (`entityFieldDisplayName`): `Device ID`, or the
 *   field as written when `target` names no catalog (`isEntityQueryTarget`);
 * - `defaultInput`: what a stage that lists no `inputs` reads, the nearest
 *   stage before it that is not a control (`defaultInputIndex`), named as
 *   `stage` names it, or `no stage` when there is none. The stage is looked
 *   for in each definition, as the same declaration, else as a stage with
 *   its id; undefined when no definition holds it.
 * A phrase names a control by its caption, as the control bar does
 * (`controlCaptionOf`). An id no definition declares is named as something
 * not on the board.
 */
export interface DefinitionNames {
  panel(id: string): string;
  stage(id: string): string;
  stagePhrase(id: string): string;
  controlPhrase(id: string): string;
  declaredStagePhrase(stage: unknown): string;
  catalogField(target: string, field: string): string;
  defaultInput(declaration: Record<string, unknown>): string | undefined;
}

/** The org's Ready custom fields of a catalog, which name the catalog's
 *  custom fields (`entityFieldDisplayName`); `undefined` while they have not
 *  arrived. */
export type CustomFieldsOfCatalog = (
  target: EntityQueryTarget,
) => readonly CustomFieldRecord[] | undefined;

/**
 * Names each id by the first of `definitions` that declares it, in the order
 * given, so a card passes the definition an update would save before the
 * stored one, and a panel or stage the update removes is still named. A
 * field of a catalog is named with `customFieldsOf` that catalog. Read
 * defensively, since a definition is whatever the model emitted.
 */
export function definitionNames(
  customFieldsOf: CustomFieldsOfCatalog,
  ...definitions: readonly unknown[]
): DefinitionNames {
  const listOf = (member: string) =>
    definitions.map((definition) =>
      isRecord(definition) && Array.isArray(definition[member])
        ? (definition[member] as unknown[])
        : [],
    );
  const panelLists = listOf("panels");
  const stageLists = listOf("stages");
  return {
    panel(id) {
      for (const panels of panelLists) {
        const index = panels.findIndex(
          (panel) => isRecord(panel) && panel.id === id,
        );
        if (index !== -1) {
          return panelPhrase(
            (panels[index] as Record<string, unknown>).title,
            index,
          );
        }
      }
      return "a panel that is not on the board";
    },
    stage(id) {
      const stage = findStage(id);
      return stage === undefined
        ? MISSING_STAGES.absent
        : stagePhraseInList(stage);
    },
    stagePhrase(id) {
      const stage = findStage(id);
      return stage === undefined
        ? MISSING_STAGES.absent
        : declaredStagePhrase(stage);
    },
    controlPhrase(id) {
      const stage = findStage(id);
      return stage === undefined
        ? MISSING_CONTROLS.absent
        : controlPhraseOf(stage);
    },
    declaredStagePhrase,
    catalogField(target, field) {
      return isEntityQueryTarget(target)
        ? entityFieldDisplayName(target, field, customFieldsOf(target))
        : field;
    },
    defaultInput(declaration) {
      for (const stages of stageLists) {
        const index = positionOf(stages, declaration);
        if (index === undefined) {
          continue;
        }
        const input = defaultInputIndex(stages, index, isControlStage);
        return input === undefined
          ? "no stage"
          : stagePhraseInList(stages[input]);
      }
      return undefined;
    },
  };

  /**
   * A stage as a phrase of its own: `the stage "Speeds"`, or `a stage with
   * no name`; a control by its caption: `the control "Region"`, or `a
   * control with no name`.
   */
  function declaredStagePhrase(stage: unknown): string {
    if (isControlStage(stage)) {
      return controlPhraseOf(stage);
    }
    const name = declaredStageName(stage);
    return name === undefined ? MISSING_STAGES.unnamed : `the stage "${name}"`;
  }

  /** A control as a phrase: `the control "Region"`, or `a control with no
   *  name`. */
  function controlPhraseOf(stage: Record<string, unknown>): string {
    const caption = controlCaptionOf(stage, customFieldsOf);
    return caption === undefined
      ? MISSING_CONTROLS.unnamed
      : `the control "${caption}"`;
  }

  /** A stage of a definition's list, as `stage` names it. */
  function stagePhraseInList(stage: unknown): string {
    const name = declaredStageName(stage);
    return name === undefined ? MISSING_STAGES.unnamed : `"${name}"`;
  }

  /** The stage `id`, from the first definition that declares it. */
  function findStage(id: string): Record<string, unknown> | undefined {
    for (const stages of stageLists) {
      const stage = stages.find((entry) => isRecord(entry) && entry.id === id);
      if (isRecord(stage)) {
        return stage;
      }
    }
    return undefined;
  }
}

/** Where `declaration` is in `stages`: the same object, else the first
 *  stage with its id. */
function positionOf(
  stages: readonly unknown[],
  declaration: Record<string, unknown>,
): number | undefined {
  const same = stages.indexOf(declaration);
  if (same !== -1) {
    return same;
  }
  const { id } = declaration;
  const byId =
    typeof id === "string"
      ? stages.findIndex((stage) => isRecord(stage) && stage.id === id)
      : -1;
  return byId === -1 ? undefined : byId;
}

/** Whether a stage is a control: a value control or a field control. */
function isControlStage(
  stage: unknown,
): stage is Record<string, unknown> & { body: Record<string, unknown> } {
  return (
    isRecord(stage) &&
    isRecord(stage.body) &&
    (stage.body.kind === "value-control" || stage.body.kind === "field-control")
  );
}

/**
 * A control's caption, as the control bar shows it (`controlCaption`): its
 * label when the label is not blank; otherwise, for a value control whose
 * choices are a field of a catalog, that field's display name
 * (`entityFieldDisplayName`, with `customFieldsOf` the catalog), or the
 * field as written when the domain's target names no catalog; otherwise
 * its name when it is not blank.
 */
function controlCaptionOf(
  stage: Record<string, unknown>,
  customFieldsOf: CustomFieldsOfCatalog,
): string | undefined {
  const body = isRecord(stage.body) ? stage.body : {};
  if (typeof body.label === "string" && body.label.trim() !== "") {
    return body.label;
  }
  const domain = body.domain;
  if (
    body.kind === "value-control" &&
    isRecord(domain) &&
    domain.kind === "entity-field" &&
    typeof domain.target === "string" &&
    typeof domain.field === "string"
  ) {
    return isEntityQueryTarget(domain.target)
      ? entityFieldDisplayName(
          domain.target,
          domain.field,
          customFieldsOf(domain.target),
        )
      : domain.field;
  }
  return declaredStageName(stage);
}

/** Why a stage has no name to show: it has none, or no definition declares
 *  it. */
type MissingName = "unnamed" | "absent";

const MISSING_STAGES: Readonly<Record<MissingName, string>> = {
  unnamed: "a stage with no name",
  absent: "a stage that is not on the board",
};

const MISSING_CONTROLS: Readonly<Record<MissingName, string>> = {
  unnamed: "a control with no name",
  absent: "a control that is not on the board",
};

/**
 * One proposed panel as a noun phrase that follows a verb: `a table titled
 * "Sessions" reading sessions`. Read defensively, since the proposal is
 * whatever the model emitted. Stages come only from `bindings`, so a chart
 * carrying `template` and no `spec`, whose bindings the authoring service
 * emits when it expands the template, is described without them.
 */
export function describePanel(panel: unknown, names: DefinitionNames): string {
  if (!isRecord(panel)) {
    return "a panel that can't be read";
  }
  const kind = typeof panel.kind === "string" ? panel.kind : "panel of no kind";
  const stages = boundStageIdsOf(panel).map((id) => names.stage(id));
  return stages.length === 0
    ? panelNoun(kind, panel)
    : `${panelNoun(kind, panel)} reading ${listPhrase(stages)}`;
}

/** The stage ids an unparsed panel binds, off its `bindings`, read
 *  defensively since the list is whatever the model emitted. */
function boundStageIdsOf(record: Record<string, unknown>): string[] {
  if (!Array.isArray(record.bindings)) {
    return [];
  }
  const ids: string[] = [];
  for (const binding of record.bindings) {
    const id = isRecord(binding) ? binding.stage : undefined;
    if (typeof id === "string" && !ids.includes(id)) {
      ids.push(id);
    }
  }
  return ids;
}

/** {@link describePanel} capitalized to open a line of its own. */
export function describePanelLine(
  panel: unknown,
  names: DefinitionNames,
): string {
  return capitalizeFirst(describePanel(panel, names));
}

function panelNoun(kind: string, record: Record<string, unknown>): string {
  if (typeof record.title === "string" && record.title.trim() !== "") {
    return `a ${kind} titled "${record.title.trim()}"`;
  }
  return `an untitled ${kind}`;
}

/** The id an unparsed panel or stage declares, for a card's dry-run status. */
export function declarationIdOf(declaration: unknown): string | undefined {
  return isRecord(declaration) && typeof declaration.id === "string"
    ? declaration.id
    : undefined;
}

/** The words for each participation mode, after the words that name the
 *  selection (`selectionPhrase`). */
const PARTICIPATION_PHRASES: Readonly<Record<string, string>> = {
  filter: "filters its rows",
  highlight: "highlights the rows it matches",
  ignore: "has no effect on it",
};

/**
 * One clause per selection of a proposed `participation` member, in the
 * member's own order, each selection named by `selectionPhrase`: `what is
 * picked filters its rows`, `the range dragged across a chart highlights the
 * rows it matches`, `the selection “region” has no effect on it`. An empty
 * member puts every selection back to the default, and says so. A mode other
 * than the three is named as written and said not to be a valid setting, so
 * the card names every value a person approves.
 */
function describeParticipation(participation: unknown): string[] {
  if (!isRecord(participation)) {
    return ["how it responds to selections can't be read"];
  }
  const entries = Object.entries(participation);
  if (entries.length === 0) {
    return ["every selection affects it as it does by default"];
  }
  return entries.map(([selection, mode]) => {
    const words =
      typeof mode === "string" ? PARTICIPATION_PHRASES[mode] : undefined;
    return words === undefined
      ? `how ${selectionPhrase(selection)} affects it is set to ${asWritten(mode)}, which is not a valid setting`
      : `${selectionPhrase(selection)} ${words}`;
  });
}

/**
 * One clause per selection of a proposed `publish` member, each selection
 * named by `selectionPhrase`: `what a viewer selects in it sets what is
 * picked across the whole board` for `outer`. An empty member leaves each
 * gesture publishing into the section's selection of that name, or the
 * board's where the panel's section declares none, and any other value is
 * named as written and said not to be a valid setting.
 */
function describePublish(publish: unknown): string[] {
  if (!isRecord(publish)) {
    return ["which selections it sets can't be read"];
  }
  const entries = Object.entries(publish);
  if (entries.length === 0) {
    return [
      "what a viewer selects in it sets its section's selections, or the board's where the section has none of its own",
    ];
  }
  return entries.map(([selection, target]) =>
    target === "outer"
      ? `what a viewer selects in it sets ${selectionPhrase(selection)} across the whole board`
      : `where ${selectionPhrase(selection)} from it goes is set to ${asWritten(target)}, which is not a valid setting`,
  );
}

/** A value the model sent as it was written, for a card that must name it. */
function asWritten(value: unknown): string {
  return JSON.stringify(value) ?? String(value);
}

/**
 * A `set_participation_and_publish` operation as one sentence: `Change how
 * the panel "Route" works with selections: what is picked has no effect on
 * it; what a viewer selects in it sets what is picked across the whole
 * board`. A member sent as null reads as returning to the default; a member
 * the operation leaves out is not mentioned, since the update leaves it as
 * stored.
 */
export function describeSetParticipation(
  operation: {
    panel_id: string;
    participation?: unknown;
    publish?: unknown;
  },
  names: DefinitionNames,
): string {
  const clauses: string[] = [];
  if (operation.participation === null) {
    clauses.push("how selections affect it returns to the default");
  } else if (operation.participation !== undefined) {
    clauses.push(...describeParticipation(operation.participation));
  }
  if (operation.publish === null) {
    clauses.push("where its selections go returns to the default");
  } else if (operation.publish !== undefined) {
    clauses.push(...describePublish(operation.publish));
  }
  return `Change how ${names.panel(operation.panel_id)} works with selections: ${clauses.join("; ")}`;
}

/**
 * A `set_panels_filtered_by_control` operation as one sentence, naming the
 * control by its stage's name and the panels and stages by `names`: `Make
 * the control "Site" filter only the panel "Map" and the panel "Speeds" (the
 * panel "Speeds", stage "Joined": column "site")`, or `Make the control "Site"
 * filter no panels`. The list is the whole set, so a panel it leaves out
 * stops being filtered. A patched request's card words the operation by the
 * control's caption and what each panel is matched on instead
 * (`PatchOutcome.operationSentences`).
 */
export function describeSetPanelsFilteredByControl(
  operation: {
    control_id: string;
    panel_ids: readonly string[];
    columns?: readonly (
      | { panel_id: string; stage_id: string; column: string }
      | { panel_id: string; stage_id: string; leave_unfiltered: true }
    )[];
  },
  names: DefinitionNames,
): string {
  const panels = operation.panel_ids.map((id) => names.panel(id));
  const listed =
    panels.length === 0 ? "no panels" : `only ${listPhrase(panels)}`;
  const columns = (operation.columns ?? []).map((choice) => {
    const where = `${names.panel(choice.panel_id)}, ${names.stagePhrase(choice.stage_id)}`;
    return "column" in choice
      ? `${where}: column "${choice.column}"`
      : `${where}: left unfiltered`;
  });
  return `Make ${names.controlPhrase(operation.control_id)} filter ${listed}${columns.length === 0 ? "" : ` (${columns.join("; ")})`}`;
}

/** The line of a stage or panel the check did not run because the controls
 *  `captions` have no value: what it needs, naming each control once. */
function notCheckedLine(captions: readonly string[]): string {
  const quoted = [...new Set(captions)].map((caption) => `"${caption}"`);
  return `not checked: needs a value for ${listPhrase(quoted)}`;
}

/** Phrases joined as a person lists them: `a`, `a and b`, `a, b and c`. */
export function listPhrase(phrases: readonly string[]): string {
  return phrases.length <= 1
    ? phrases.join("")
    : `${phrases.slice(0, -1).join(", ")} and ${phrases.at(-1)}`;
}

/**
 * The proposed sections against the stored ones, one entry per proposed
 * section by title: `(own selections: what is picked)` when it declares
 * selections of its own, `(no longer has its own selections)` when the stored section
 * declared some and the proposal declares none, `(added)` when no stored
 * section carries its id, `(moved or resized)` when its geometry differs
 * from the stored section's; then `removed: <titles>` for stored ids the
 * proposal drops. An update replaces the whole sections list, so the model sends the
 * stored sections back with its change: a section sent back as stored shows
 * no marker, so a proposal that changes no section reads as the titles
 * alone, and a section sent back with changed geometry or without its
 * selections says so. Read defensively, since the list is whatever the
 * model emitted; with no stored list nothing is compared.
 */
export function describeSections(
  sections: unknown[],
  stored: unknown[] | undefined,
): string {
  const storedById = new Map<string, Record<string, unknown>>();
  for (const section of stored ?? []) {
    if (isRecord(section) && typeof section.id === "string") {
      storedById.set(section.id, section);
    }
  }
  const proposedIds = new Set<string>();
  const entries = sections.map((section) => {
    if (!isRecord(section)) {
      return "a section that can't be read";
    }
    const id = typeof section.id === "string" ? section.id : undefined;
    if (id !== undefined) {
      proposedIds.add(id);
    }
    const markers: string[] = [];
    const selections = ownSelectionNames(section);
    if (selections.length > 0) {
      markers.push(
        `own selections: ${listPhrase(selections.map(selectionPhrase))}`,
      );
    }
    const storedSection = id === undefined ? undefined : storedById.get(id);
    if (stored !== undefined && storedSection === undefined) {
      markers.push("added");
    } else if (storedSection !== undefined) {
      if (
        selections.length === 0 &&
        ownSelectionNames(storedSection).length > 0
      ) {
        markers.push("no longer has its own selections");
      }
      if (!sameGeometry(section, storedSection)) {
        markers.push("moved or resized");
      }
    }
    const title = sectionTitle(section);
    return markers.length === 0 ? title : `${title} (${markers.join("; ")})`;
  });
  const removed = [...storedById.entries()]
    .filter(([id]) => !proposedIds.has(id))
    .map(([, section]) => sectionTitle(section));
  const list = entries.join(", ");
  return removed.length === 0
    ? list
    : `${list}; removed: ${removed.join(", ")}`;
}

/** The names of the selections an unparsed section declares for itself,
 *  off its `selections`. */
function ownSelectionNames(section: Record<string, unknown>): string[] {
  if (!Array.isArray(section.selections)) {
    return [];
  }
  return section.selections.flatMap((selection) =>
    isRecord(selection) && typeof selection.name === "string"
      ? [selection.name]
      : [],
  );
}

/** The keys that place and size a section on the canvas. */
const GEOMETRY_KEYS = ["x", "y", "width", "height"] as const;

function sameGeometry(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
): boolean {
  return GEOMETRY_KEYS.every((key) => a[key] === b[key]);
}

function sectionTitle(section: Record<string, unknown>): string {
  const { title } = section;
  return typeof title === "string" && title.length > 0 ? title : "(untitled)";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** The words for each kind of source but an entity query, which is named
 *  by its target. */
const SOURCE_KIND_WORDS: Readonly<
  Record<Exclude<KnownSourceKind, "entity.query">, string>
> = {
  "metric.aggregate": "a metric aggregate",
  "metric.values": "metric values",
  "topic.data": "topic data",
};

/**
 * One proposed stage on a line, its name then what it does
 * (`describeStageWork`): `Sessions: a list of sessions`, or for a stage
 * that reads other stages, named by `names`: `Per session: a SQL stage
 * reading "Sessions" and "Events"`. A stage with no name, or a blank one,
 * opens with `A stage with no name`. A control is named by its caption
 * (`names.declaredStagePhrase`): `The control "Region": holds text, typed in`,
 * its values written in `timeZone`. Read defensively, since the stage is
 * whatever the model emitted; a kind the board does not know reads `a stage
 * of a kind the board does not know`, never the kind the model wrote.
 */
export function describeStage(
  stage: unknown,
  names: DefinitionNames,
  timeZone: string,
): string {
  if (!isRecord(stage)) {
    return "a stage that can't be read";
  }
  const work = describeStageWork(stage, names, timeZone);
  if (isControlStage(stage)) {
    return `${capitalizeFirst(names.declaredStagePhrase(stage))}: ${work}`;
  }
  return `${declaredStageName(stage) ?? "A stage with no name"}: ${work}`;
}

/** What a stage does, the part of {@link describeStage} after its name:
 *  `a Group stage reading "Speeds", to aggregate rows, optionally by group`
 *  (`derivedStageWork`), or for a control `holds a number, typed in,
 *  default 5`. A stage whose `inputs` list is empty reads `no stage`, as
 *  does a Filter by control stage that lists no inputs. Any other stage
 *  that lists no inputs reads the stage `names.defaultInput` finds, and
 *  where no definition holds the stage, the card says it reads `the
 *  nearest stage before it that is not a control`. */
export function describeStageWork(
  stage: unknown,
  names: DefinitionNames,
  timeZone: string,
): string {
  if (!isRecord(stage)) {
    return "a stage that can't be read";
  }
  if (isControlStage(stage)) {
    return describeControl(stage, stage.body, names, timeZone);
  }
  const body = isRecord(stage.body) ? stage.body : undefined;
  if (typeof body?.kind !== "string") {
    return "a stage of no kind";
  }
  if (body.kind === "entity.query") {
    const records = typeof body.target === "string" ? body.target : "records";
    return body.aggregate === undefined
      ? `a list of ${records}`
      : `a count of ${records}`;
  }
  if (Object.hasOwn(SOURCE_KIND_WORDS, body.kind)) {
    return SOURCE_KIND_WORDS[body.kind as keyof typeof SOURCE_KIND_WORDS];
  }
  if (!isDerivedOrControlFilterKind(body.kind)) {
    return "a stage of a kind the board does not know";
  }
  const inputs = Array.isArray(stage.inputs)
    ? stage.inputs.filter((input): input is string => typeof input === "string")
    : [];
  // A control-filter reads the stage whose rows it keeps, then the control.
  const control = body.kind === "control-filter" ? inputs[1] : undefined;
  const rowInputs =
    body.kind === "control-filter" ? inputs.slice(0, 1) : inputs;
  return derivedStageWork(
    body.kind,
    readPhrase(stage, body.kind, rowInputs, names),
    stagePurpose(
      body.kind,
      body.column,
      control === undefined ? "a control" : names.controlPhrase(control),
    ),
  );
}

/** The stages a stage of `kind` reads its rows from, named by `names`:
 *  those its `inputs` list (`rowInputs`), `no stage` for an empty list, and
 *  for a stage that lists none, the stage `names.defaultInput` finds. A
 *  Filter by control stage gets no default input (the board refuses one
 *  that lists no inputs), so one that lists none reads `no stage`. */
function readPhrase(
  stage: Record<string, unknown>,
  kind: DerivedOrControlFilterKind,
  rowInputs: readonly string[],
  names: DefinitionNames,
): string {
  if (Array.isArray(stage.inputs) || kind === "control-filter") {
    return rowInputs.length === 0
      ? "no stage"
      : listPhrase(rowInputs.map((input) => names.stage(input)));
  }
  return (
    names.defaultInput(stage) ??
    "the nearest stage before it that is not a control"
  );
}

function isDerivedOrControlFilterKind(
  kind: string,
): kind is DerivedOrControlFilterKind {
  return Object.hasOwn(STAGE_KIND_NAMES, kind);
}

/**
 * A stage of `kind` that reads the stages named as `read`, in the words of
 * the panel editor's Add stage menu (`stageKindWords`): the kind's name,
 * then `purpose` where there is one: `a Group stage reading "Sessions", to
 * aggregate rows, optionally by group`, or `a SQL stage reading "Sessions"
 * and "Events"`.
 */
function derivedStageWork(
  kind: DerivedOrControlFilterKind,
  read: string,
  purpose: string | undefined,
): string {
  const name = STAGE_KIND_NAMES[kind];
  const article = /^[AEIOU]/.test(name) ? "an" : "a";
  const stage = `${article} ${name} stage reading ${read}`;
  return purpose === undefined ? stage : `${stage}, to ${purpose}`;
}

/** What a stage of `kind` does, as the Add stage menu says it: for a Filter
 *  by control stage, keeping the rows whose `column` matches what a viewer
 *  chooses in `control`; undefined for a SQL or Combine stage, which the
 *  menu does not describe. */
function stagePurpose(
  kind: DerivedOrControlFilterKind,
  column: unknown,
  control: string,
): string | undefined {
  switch (kind) {
    case "control-filter":
      return controlFilterPurpose(
        control,
        typeof column === "string" ? column : undefined,
      );
    case "sql":
    case "combine":
      return undefined;
    default:
      return SINGLE_INPUT_STAGE_PURPOSES[kind];
  }
}

/** What a field control chooses from, in words: `a field of sessions`, or
 *  `a field of the topic "gps"`. */
function fieldDomainPhrase(body: Record<string, unknown>): string {
  if (typeof body.topic === "string") {
    return `a field of the topic "${body.topic}"`;
  }
  return typeof body.target === "string"
    ? `a field of ${body.target}`
    : "a field";
}

/** Where a value control's choices come from, in words: typed in, a list,
 *  a column of the stage it reads (named by `names`), or a field of a kind
 *  of record. */
function valueDomainPhrase(
  stage: Record<string, unknown>,
  domain: unknown,
  names: DefinitionNames,
): string {
  if (!isRecord(domain)) {
    return "typed in";
  }
  if (domain.kind === "column") {
    const input =
      Array.isArray(stage.inputs) && typeof stage.inputs[0] === "string"
        ? names.stage(stage.inputs[0])
        : "the stage it reads";
    const column =
      typeof domain.column === "string"
        ? `the column "${domain.column}"`
        : "a column";
    return `chosen from ${column} of ${input}`;
  }
  if (domain.kind === "entity-field") {
    return typeof domain.target === "string"
      ? `chosen from the values of a field of ${domain.target}`
      : "chosen from the values of a field";
  }
  return "chosen from a list";
}

/**
 * A control's body as the viewer will see the control, after the phrase
 * that names it: `holds text, a viewer can choose several, chosen from the
 * values of a field of sessions`, `holds a number, typed in, required,
 * default 5`, or for a control that chooses a field, `chooses a field of
 * sessions`. A default is written by `fieldControlDefaultText` or
 * `controlDefaultText`.
 */
function describeControl(
  stage: Record<string, unknown>,
  body: Record<string, unknown>,
  names: DefinitionNames,
  timeZone: string,
): string {
  const parts: string[] = [];
  if (body.kind === "field-control") {
    parts.push(`chooses ${fieldDomainPhrase(body)}`);
  } else {
    const valueType = interactionValueTypeSchema.safeParse(body.valueType);
    parts.push(
      valueType.success
        ? `holds ${INTERACTION_VALUE_TYPE_WORDS[valueType.data.type]}`
        : "holds values of a type the board does not know",
    );
    if (body.multi === true) {
      parts.push("a viewer can choose several");
    }
    parts.push(valueDomainPhrase(stage, body.domain, names));
  }
  if (body.required === true) {
    parts.push("required");
  }
  const defaultText =
    body.kind === "field-control"
      ? fieldControlDefaultText(body, names)
      : controlDefaultText(body, timeZone);
  if (defaultText !== undefined) {
    parts.push(`default ${defaultText}`);
  }
  return parts.join(", ");
}

/**
 * A field control's default as the control bar shows the field it holds:
 * for a control that lists options, the label of the option that holds the
 * field, or the field as written when none does or its label is blank
 * (`listedOptionLabel`); else, for a field of a catalog, the field's
 * display name (`names.catalogField`); else, for a topic's field, its path.
 * Undefined when the control has no default, which the board takes empty
 * text to be.
 */
function fieldControlDefaultText(
  body: Record<string, unknown>,
  names: DefinitionNames,
): string | undefined {
  const field = body.default;
  if (typeof field !== "string" || isEmptyText(field)) {
    return undefined;
  }
  if (Array.isArray(body.options)) {
    const option = body.options.find(
      (entry): entry is { value: unknown; label: string } =>
        isRecord(entry) &&
        entry.value === field &&
        typeof entry.label === "string",
    );
    const label = option === undefined ? undefined : listedOptionLabel(option);
    return label ?? field;
  }
  return typeof body.target === "string"
    ? names.catalogField(body.target, field)
    : field;
}

/**
 * A value control's default as the control bar writes it: each value,
 * converted to the control's type, by the label its listed options give it
 * (`listedLabelOf`), else as `interactionValueText` writes it, an instant in
 * `timeZone`; several are joined with ", ". An entry that is not a value
 * reads `a value the board can't read`. Undefined when the control has no
 * default, which the board takes an empty list or empty text to be.
 */
function controlDefaultText(
  body: Record<string, unknown>,
  timeZone: string,
): string | undefined {
  const declared = Array.isArray(body.default) ? body.default : [body.default];
  const defaults = declared.filter(
    (entry) => entry !== undefined && !isEmptyText(entry),
  );
  if (defaults.length === 0) {
    return undefined;
  }
  const valueType = interactionValueTypeSchema.safeParse(body.valueType);
  const options =
    isRecord(body.domain) &&
    body.domain.kind === "static" &&
    Array.isArray(body.domain.options)
      ? body.domain.options.filter(
          (entry): entry is { value: unknown; label: string } =>
            isRecord(entry) && typeof entry.label === "string",
        )
      : [];
  return defaults
    .map((entry) => {
      const parsed = parseInteractionValue(entry);
      if (parsed.status !== "valid") {
        return "a value the board can't read";
      }
      if (!valueType.success) {
        return interactionValueText(parsed.value, timeZone);
      }
      const converted = convertInteractionValue(parsed.value, valueType.data);
      const value =
        converted.status === "valid" ? converted.value : parsed.value;
      return (
        listedLabelOf(options, valueType.data, value) ??
        interactionValueText(value, timeZone)
      );
    })
    .join(", ");
}

/**
 * A definition's `time_range` in words: a preset's full label (`Last 90 days`, the
 * accessible name of the header's short toggle such as `3M`), the two
 * bounds of a custom range and the time zone of `calendar` they are read
 * in, or a relative range's amount and unit (`Last 7 days`). A preset
 * string the header would not recognize is shown as written.
 */
export function describeTimeRange(
  timeRange: unknown,
  calendar: BoardCalendar,
): string | undefined {
  const mode = asBoardTimeRangeMode(timeRange);
  if (mode !== null) {
    return boardTimeRangeLabel(mode, calendar);
  }
  if (typeof timeRange === "string") {
    return timeRange;
  }
  if (
    isRecord(timeRange) &&
    typeof timeRange.start === "string" &&
    typeof timeRange.end === "string"
  ) {
    return `${timeRange.start} to ${timeRange.end}`;
  }
  return undefined;
}

/** The calendar a definition's `calendar` member declares, or the default
 *  calendar when the member is absent or is not one a definition takes. */
export function calendarOfDefinitionMember(calendar: unknown): BoardCalendar {
  const declared = declaredBoardCalendarSchema.safeParse(calendar);
  return boardCalendarOf(declared.success ? declared.data : undefined);
}

/** A definition's `calendar` member in words. */
export interface CalendarDescription {
  /** The time zone and week start, such as
   *  `Los Angeles (Pacific Time), weeks start on Sunday`. A time zone the
   *  board cannot use is written as the request gives it, in quotes. Empty
   *  when no part of the member can be read. */
  text: string;
  /** Why the board cannot use the member, when it cannot. */
  problem?: string;
}

/** A definition's `calendar` member in words, or undefined when the value
 *  is absent. */
export function describeCalendar(
  calendar: unknown,
): CalendarDescription | undefined {
  if (calendar === undefined) {
    return undefined;
  }
  const declared = declaredBoardCalendarSchema.safeParse(calendar);
  if (declared.success) {
    const { timeZone, weekStart } = boardCalendarOf(declared.data);
    return {
      text: `${timeZoneChoiceLabel(timeZone)}, ${weekStartText(weekStart)}`,
    };
  }
  return describeRefusedCalendar(calendar);
}

/** A `calendar` member the board cannot use: the parts of it that can be
 *  read, and why it is refused. */
function describeRefusedCalendar(calendar: unknown): CalendarDescription {
  const members = isRecord(calendar) ? calendar : {};
  const timeZone =
    typeof members.time_zone === "string" ? members.time_zone : undefined;
  const weekStart = DAYS_OF_WEEK.find((day) => day === members.week_start);
  const zoneProblem =
    timeZone === undefined ? undefined : timeZoneProblem(timeZone);
  const parts = [
    timeZone === undefined ? undefined : zoneText(timeZone, zoneProblem),
    weekStart === undefined ? undefined : weekStartText(weekStart),
  ].filter((part) => part !== undefined);
  return {
    text: parts.join(", "),
    problem:
      zoneProblem ??
      "This is not a time zone and week start the board can use.",
  };
}

/** A requested time zone in words: its city and name, or, when the board
 *  cannot use it (`problem` says why), the name as given, in quotes. */
function zoneText(timeZone: string, problem: string | undefined): string {
  return problem === undefined
    ? timeZoneChoiceLabel(timeZone)
    : `"${timeZone}"`;
}

function weekStartText(weekStart: DayOfWeek): string {
  return `weeks start on ${capitalizeFirst(weekStart)}`;
}
